/**
 * Phase 7.7 — Vehicle physical reality + parallelism enforcement (pure).
 *
 * Doctrine:
 *   - Bucket 3 §3.2     Physical location is distinct from production state.
 *   - Bucket 3 §3.3.x   A vehicle occupies ONE primary physical location at
 *                       a time: bay, specialty bay, parking spot, general
 *                       lot, off-site, or unknown.
 *   - Bucket 4 §4.x     Split tickets represent separate job-line work but
 *                       do NOT imply the vehicle is in two places at once.
 *   - Bucket 4.5 §4.5.x Parallel work is allowed only when physical
 *                       location + job order + bay/specialty + tech/lane
 *                       scheduling all support it.
 *   - Bucket 5 §5.x     Specialty job order is hard-constrained:
 *                       diagnostics → repairs → alignment → ADAS → wash.
 *                       Same stage allows parallel; later stage waits.
 *
 * Why this module exists
 * ----------------------
 * Read-first survey at Phase 7.7 confirmed:
 *
 *   - `parkingToBayTimerStart.ts:getTimerStartLocationState` is the
 *     canonical 4-state classifier for a SINGLE ticket
 *     (`in_bay` / `in_parking_spot` / `in_general_lot` / `no_location`)
 *     with bay_id > parking_spot_id > parking_lot precedence.
 *   - `physicalLocationVisibility.ts` (Phase 3.4) re-exports it plus
 *     lifecycle exclusion (`picked_up_at`/`deleted_at`/`archived_at`).
 *   - `specialtyJobOrderValidator.ts` has the canonical 5-stage job-order
 *     model (diagnostics/repairs/alignment/ADAS/wash) with
 *     `canStartSpecialtyJob` + `getBlockingPrerequisites`.
 *   - NO existing helper cross-checks SAME-vehicle physical conflicts
 *     across multiple tickets. Two split siblings sharing a vehicle
 *     could each carry different incompatible locations — the
 *     impossible-physical-reality risk surface Bucket 3/4 doctrine
 *     forbids.
 *
 * Phase 7.7 supplies the canonical cross-ticket validator + parallelism
 * verdict + blocker vocabulary so a future preflight wrapper around
 * cascade / smart-assign / split creation can refuse impossible
 * physical reality WITHOUT rewriting any runtime.
 *
 * Phase 7.7 scope: HELPER + TESTS ONLY.
 *   - Pure. No DB I/O, no clock reads, no React hooks. Caller passes
 *     in-memory ticket projections plus optional job-line context.
 *   - No DB migration / no schema change. Specialty-bay view-entry
 *     distinction comes from caller context (`viewOnly?: boolean`),
 *     NOT a new DB column. Per directive: "Do not create
 *     specialty_bay_views table yet."
 *   - No runtime wiring. Cascade, smart-assign, AutoBump, split
 *     creation, bay occupancy, and ticket movement paths are
 *     untouched.
 *
 * Builds on:
 *   - Phase 2.4 `parkingToBayTimerStart.getTimerStartLocationState`
 *     (single-ticket 4-state location).
 *   - Phase 3.4 `physicalLocationVisibility` (lifecycle exclusion).
 *   - Phase 7.1 `splitGroupIdentity.isRolloverContinuationNotJobLineSplit`
 *     (rollover/job-line distinction).
 *   - Phase 4.3 `dualCapacityModel` (`bay_type_mismatch` blocker
 *     vocabulary).
 *   - Specialty job-order validator (5-stage parallel/sequential).
 */

import {
  getTimerStartLocationState,
  type TimerStartLocationState,
} from './parkingToBayTimerStart';
import type { ProductionStateTicketShape } from './ticketProductionState';
import { isRolloverContinuationNotJobLineSplit } from './splitGroupIdentity';
import { getJobStage } from './specialtyJobOrderValidator';
import { isTicketDeleted } from '@/utils/ticketDeletedState';

// =============================================================================
// Vocabulary
// =============================================================================

/**
 * Discrete vehicle physical-location states (Phase 7.7 superset of the
 * Phase 2.4 4-state model). Adds specialty-bay, off-site, view-only,
 * transition, and explicit "invalid mixed" classifications.
 */
export type VehiclePhysicalLocationState =
  | 'in_primary_bay'
  | 'in_specialty_bay'
  | 'in_parking_spot'
  | 'in_general_lot'
  | 'off_site'
  | 'view_only'
  | 'transition_state'
  | 'unknown_location'
  | 'invalid_mixed_location';

export const VEHICLE_PHYSICAL_LOCATION_STATES = Object.freeze([
  'in_primary_bay',
  'in_specialty_bay',
  'in_parking_spot',
  'in_general_lot',
  'off_site',
  'view_only',
  'transition_state',
  'unknown_location',
  'invalid_mixed_location',
] as const) satisfies ReadonlyArray<VehiclePhysicalLocationState>;

/**
 * Discrete blocker / warning codes emitted by the validators.
 */
export type PhysicalRealityBlocker =
  | 'vehicle_in_multiple_physical_bays'
  | 'vehicle_in_bay_and_parking'
  | 'incompatible_specialty_bay_overlap'
  | 'split_parts_imply_impossible_parallel_location'
  | 'missing_vehicle_identity'
  | 'missing_location_context'
  | 'bay_type_mismatch'
  | 'required_bay_unavailable'
  | 'job_order_prerequisite_not_met'
  | 'specialty_view_not_physical_location'
  | 'parking_to_bay_confirmation_required'
  | 'bay_to_parking_move_requires_stop_or_pause'
  | 'physical_overlap_allowed'
  | 'view_overlap_allowed'
  | 'unknown_parallelism_policy';

export const PHYSICAL_REALITY_BLOCKERS = Object.freeze([
  'vehicle_in_multiple_physical_bays',
  'vehicle_in_bay_and_parking',
  'incompatible_specialty_bay_overlap',
  'split_parts_imply_impossible_parallel_location',
  'missing_vehicle_identity',
  'missing_location_context',
  'bay_type_mismatch',
  'required_bay_unavailable',
  'job_order_prerequisite_not_met',
  'specialty_view_not_physical_location',
  'parking_to_bay_confirmation_required',
  'bay_to_parking_move_requires_stop_or_pause',
  'physical_overlap_allowed',
  'view_overlap_allowed',
  'unknown_parallelism_policy',
] as const) satisfies ReadonlyArray<PhysicalRealityBlocker>;

/**
 * Discrete overlap verdicts. The two `allowed_*` codes mean the pair
 * MAY coexist; the `blocked_*` codes mean they may NOT;
 * `requires_manual_review` means the helper cannot resolve safely
 * without explicit caller input.
 */
export type ParallelismVerdict =
  | 'allowed_physical_overlap'
  | 'allowed_view_overlap'
  | 'blocked_impossible_location'
  | 'blocked_bay_incompatibility'
  | 'blocked_job_order'
  | 'blocked_missing_context'
  | 'requires_manual_review';

export const PARALLELISM_VERDICTS = Object.freeze([
  'allowed_physical_overlap',
  'allowed_view_overlap',
  'blocked_impossible_location',
  'blocked_bay_incompatibility',
  'blocked_job_order',
  'blocked_missing_context',
  'requires_manual_review',
] as const) satisfies ReadonlyArray<ParallelismVerdict>;

// =============================================================================
// Input shape
// =============================================================================

/**
 * Permissive ticket shape for physical-reality analysis. Reads only
 * the fields named here; everything else is ignored.
 *
 * Caller-supplied signals:
 *   - `view_only`: when true, the ticket is a non-physical specialty
 *      view entry (Phase 7.8 will decide whether to model these as a
 *      separate concept; Phase 7.7 accepts the flag from callers that
 *      already track it).
 *   - `transition_state`: when true, the ticket is between locations
 *      (e.g., mid-pull-from-parking). Allows momentarily-mixed flags
 *      without firing the invalid-mixed-location classifier.
 *   - `off_site`: when true, the vehicle is at a sublet / off-site
 *      provider. Does NOT collide with on-site physical positions.
 */
export interface VehiclePhysicalRealityShape extends ProductionStateTicketShape {
  id?: string | null;
  vehicle_id?: string | null;
  bay_id?: string | null;
  bay_type?: string | null;
  /** Specialty bay reference (lane / specialty entry). */
  specialty_bay_id?: string | null;
  lane_id?: string | null;
  parking_spot_id?: string | null;
  parking_lot?: boolean | null;
  /** Caller-supplied flags. */
  off_site?: boolean | null;
  view_only?: boolean | null;
  transition_state?: boolean | null;
  /** Required bay type for the work (used by `bay_type_mismatch`). */
  required_bay_type?: string | null;
  /** Specialty job category for job-order resolution. */
  job_category?: string | null;
  status?: string | null;
  picked_up_at?: string | Date | null;
  deleted_at?: string | Date | null;
  archived_at?: string | Date | null;
  /** Optional scheduling window for overlap check. */
  start_time?: string | Date | null;
  scheduled_start_time?: string | Date | null;
  opti_finish?: string | Date | null;
  predicted_minutes?: number | null;
  /** Split lineage (Phase 7.1). */
  split_group_id?: string | null;
  split_parent_id?: string | null;
  split_sequence?: number | null;
  rollover_of_ticket_id?: string | null;
  parent_ticket_id?: string | null;
}

// =============================================================================
// Internal helpers
// =============================================================================

function parseTs(v: Date | string | null | undefined): number | null {
  if (v == null) return null;
  if (v instanceof Date) {
    const t = v.getTime();
    return Number.isFinite(t) ? t : null;
  }
  const t = Date.parse(String(v));
  return Number.isFinite(t) ? t : null;
}

function pushUnique<T>(arr: T[], v: T): void {
  if (!arr.includes(v)) arr.push(v);
}

function isTerminalForPhysicalReality(t: VehiclePhysicalRealityShape): boolean {
  if (t.picked_up_at != null) return true;
  if (t.deleted_at != null) return true;
  if (t.archived_at != null) return true;
  if (isTicketDeleted(t as any) || t.status === 'Cancelled') return true;
  return false;
}

/**
 * Resolve the ticket's window for overlap math. Falls back to a
 * "treat as concurrent" sentinel when neither start nor end is
 * available — callers can opt into stricter resolution by passing
 * `requireSchedulingWindow: true` in the overlap call.
 */
interface TimeWindow {
  startMs: number | null;
  endMs: number | null;
}

function getTicketWindow(t: VehiclePhysicalRealityShape): TimeWindow {
  const startMs = parseTs(t.start_time) ?? parseTs(t.scheduled_start_time);
  let endMs: number | null = parseTs(t.opti_finish);
  if (endMs == null && startMs != null && typeof t.predicted_minutes === 'number') {
    endMs = startMs + Math.max(0, t.predicted_minutes) * 60_000;
  }
  return { startMs, endMs };
}

function windowsOverlap(a: TimeWindow, b: TimeWindow): boolean {
  // Treat missing window as "always overlapping" — defensive bias toward
  // catching impossible reality rather than silently allowing it.
  const aStart = a.startMs ?? -Infinity;
  const aEnd = a.endMs ?? Infinity;
  const bStart = b.startMs ?? -Infinity;
  const bEnd = b.endMs ?? Infinity;
  return aStart < bEnd && bStart < aEnd;
}

// =============================================================================
// Single-ticket physical-location classifier
// =============================================================================

/**
 * Resolve the canonical Phase 7.7 physical-location state for a
 * single ticket. Pure.
 *
 * Resolution order (terminal-first; first match wins):
 *
 *   1. `view_only === true` → `view_only`
 *   2. `transition_state === true` → `transition_state`
 *   3. `off_site === true` → `off_site`
 *   4. Detect invalid mixed: bay_id set + (parking_spot_id OR
 *      parking_lot=true OR specialty_bay_id) → `invalid_mixed_location`
 *      (unless `transition_state === true` already handled above).
 *      Also: specialty_bay_id set + (parking_spot_id OR parking_lot
 *      OR bay_id) is mixed.
 *   5. `bay_id` non-null → `in_primary_bay`
 *   6. `specialty_bay_id` non-null → `in_specialty_bay`
 *   7. `parking_spot_id` non-null → `in_parking_spot`
 *   8. `parking_lot === true` → `in_general_lot`
 *   9. otherwise → `unknown_location`
 */
export function getVehiclePhysicalLocationState(
  ticket: VehiclePhysicalRealityShape,
): VehiclePhysicalLocationState {
  // 1-3. Caller-supplied state flags (take precedence over derived).
  if (ticket.view_only === true) return 'view_only';
  if (ticket.transition_state === true) return 'transition_state';
  if (ticket.off_site === true) return 'off_site';

  // 4. Invalid mixed-location detection.
  const hasBay = ticket.bay_id != null;
  const hasSpecialty = ticket.specialty_bay_id != null;
  const hasSpot = ticket.parking_spot_id != null;
  const hasLot = ticket.parking_lot === true;
  let primaryCount = 0;
  if (hasBay) primaryCount++;
  if (hasSpecialty) primaryCount++;
  if (hasSpot) primaryCount++;
  if (hasLot && !hasSpot && !hasBay && !hasSpecialty) primaryCount++;
  // hasLot + (hasSpot OR hasBay OR hasSpecialty) is the mixed signal.
  const lotMixedWithOther = hasLot && (hasSpot || hasBay || hasSpecialty);
  if (primaryCount > 1 || lotMixedWithOther) {
    return 'invalid_mixed_location';
  }

  // 5-8. Single-primary resolution.
  if (hasBay) return 'in_primary_bay';
  if (hasSpecialty) return 'in_specialty_bay';
  if (hasSpot) return 'in_parking_spot';
  if (hasLot) return 'in_general_lot';

  return 'unknown_location';
}

/**
 * Convenience: does THIS ticket carry a physical (non-view) location
 * that would conflict with another physical ticket for the same
 * vehicle?
 */
export function isPhysicalLocation(state: VehiclePhysicalLocationState): boolean {
  return (
    state === 'in_primary_bay' ||
    state === 'in_specialty_bay' ||
    state === 'in_parking_spot' ||
    state === 'in_general_lot'
  );
}

// =============================================================================
// Pair-level overlap verdict
// =============================================================================

export interface OverlapVerdictContext {
  /**
   * When true, two `in_specialty_bay` entries for the same vehicle in
   * the same specialty bay are treated as a single physical position
   * (the second is implicitly a view-entry). Defaults to false — the
   * caller must opt in via Phase 7.8 view-entry semantics.
   */
  treatSpecialtyDuplicateAsView?: boolean;
  /**
   * When true, the helper enforces specialty job-order across the
   * pair using each ticket's `job_category`. Same stage → parallel
   * allowed; different stage → later-stage blocked until earlier
   * completes. Defaults to false (job-order is informational unless
   * the caller wants it enforced).
   */
  enforceJobOrder?: boolean;
}

export interface OverlapVerdictResult {
  verdict: ParallelismVerdict;
  blockers: PhysicalRealityBlocker[];
}

/**
 * Decide whether two tickets for (presumably) the SAME vehicle may
 * coexist physically + temporally. Pure.
 *
 * Resolution:
 *   1. Different vehicle ids → physically independent, allowed.
 *   2. Missing vehicle id on either → `blocked_missing_context` +
 *      `missing_vehicle_identity`.
 *   3. Terminal lifecycle on either (picked_up / deleted / cancelled /
 *      archived) → allowed (no live conflict).
 *   4. Same vehicle + non-overlapping windows → allowed.
 *   5. Either side carries `view_only` → `allowed_view_overlap`.
 *   6. Either side `transition_state` → `requires_manual_review`
 *      (caller must inspect transition semantics).
 *   7. Either side `off_site` → allowed (off-site does not collide
 *      with on-site).
 *   8. Both `in_primary_bay` → `blocked_impossible_location` +
 *      `vehicle_in_multiple_physical_bays`.
 *   9. One `in_primary_bay` and other in parking-spot/general-lot →
 *      `blocked_impossible_location` + `vehicle_in_bay_and_parking`.
 *  10. Both `in_specialty_bay` with same id (and
 *      `treatSpecialtyDuplicateAsView === true`) → `allowed_view_overlap`.
 *  11. Both `in_specialty_bay` with different ids → `blocked_bay_incompatibility`
 *      + `incompatible_specialty_bay_overlap`.
 *  12. One `in_primary_bay` and other `in_specialty_bay` →
 *      `blocked_impossible_location` (vehicle cannot be in two bay
 *      types at once unless one is view-only).
 *  13. Both parking (spot/lot) → allowed (parking is the same physical
 *      space, even if signals differ).
 *  14. Either side `invalid_mixed_location` → `blocked_impossible_location`
 *      + appropriate blocker.
 *  15. Either side `unknown_location` → `blocked_missing_context` +
 *      `missing_location_context`.
 *  16. Job-order (when `enforceJobOrder === true`): if a later-stage
 *      job is paired with an incomplete earlier-stage job on the same
 *      vehicle → `blocked_job_order` + `job_order_prerequisite_not_met`.
 */
export function canTicketsOverlapPhysically(
  a: VehiclePhysicalRealityShape,
  b: VehiclePhysicalRealityShape,
  context?: OverlapVerdictContext,
): OverlapVerdictResult {
  const blockers: PhysicalRealityBlocker[] = [];

  // 1-2. Identity check.
  if (a.vehicle_id == null || b.vehicle_id == null) {
    return {
      verdict: 'blocked_missing_context',
      blockers: ['missing_vehicle_identity'],
    };
  }
  if (a.vehicle_id !== b.vehicle_id) {
    return { verdict: 'allowed_physical_overlap', blockers: [] };
  }

  // 3. Terminal exclusion.
  if (isTerminalForPhysicalReality(a) || isTerminalForPhysicalReality(b)) {
    return { verdict: 'allowed_physical_overlap', blockers: [] };
  }

  // 4. Non-overlapping windows → no live conflict possible.
  const wa = getTicketWindow(a);
  const wb = getTicketWindow(b);
  if (!windowsOverlap(wa, wb)) {
    return { verdict: 'allowed_physical_overlap', blockers: [] };
  }

  const sa = getVehiclePhysicalLocationState(a);
  const sb = getVehiclePhysicalLocationState(b);

  // 5. View-only on either side.
  if (sa === 'view_only' || sb === 'view_only') {
    return { verdict: 'allowed_view_overlap', blockers: ['view_overlap_allowed'] };
  }

  // 6. Transition state.
  if (sa === 'transition_state' || sb === 'transition_state') {
    return {
      verdict: 'requires_manual_review',
      blockers: ['unknown_parallelism_policy'],
    };
  }

  // 7. Off-site on either side.
  if (sa === 'off_site' || sb === 'off_site') {
    return { verdict: 'allowed_physical_overlap', blockers: [] };
  }

  // 14. Invalid mixed.
  if (sa === 'invalid_mixed_location' || sb === 'invalid_mixed_location') {
    pushUnique(blockers, 'vehicle_in_bay_and_parking');
    return { verdict: 'blocked_impossible_location', blockers };
  }

  // 15. Unknown location on either side.
  if (sa === 'unknown_location' || sb === 'unknown_location') {
    return {
      verdict: 'blocked_missing_context',
      blockers: ['missing_location_context'],
    };
  }

  // 8. Both primary bay.
  if (sa === 'in_primary_bay' && sb === 'in_primary_bay') {
    pushUnique(blockers, 'vehicle_in_multiple_physical_bays');
    return { verdict: 'blocked_impossible_location', blockers };
  }

  // 9. Primary bay + parking on the other side.
  if (
    (sa === 'in_primary_bay' && (sb === 'in_parking_spot' || sb === 'in_general_lot')) ||
    (sb === 'in_primary_bay' && (sa === 'in_parking_spot' || sa === 'in_general_lot'))
  ) {
    pushUnique(blockers, 'vehicle_in_bay_and_parking');
    return { verdict: 'blocked_impossible_location', blockers };
  }

  // 10-11. Specialty bay pair.
  if (sa === 'in_specialty_bay' && sb === 'in_specialty_bay') {
    if (
      context?.treatSpecialtyDuplicateAsView === true &&
      a.specialty_bay_id != null &&
      a.specialty_bay_id === b.specialty_bay_id
    ) {
      return { verdict: 'allowed_view_overlap', blockers: ['view_overlap_allowed'] };
    }
    pushUnique(blockers, 'incompatible_specialty_bay_overlap');
    return { verdict: 'blocked_bay_incompatibility', blockers };
  }

  // 12. Primary bay + specialty bay (one is non-view).
  if (
    (sa === 'in_primary_bay' && sb === 'in_specialty_bay') ||
    (sb === 'in_primary_bay' && sa === 'in_specialty_bay')
  ) {
    pushUnique(blockers, 'vehicle_in_multiple_physical_bays');
    return { verdict: 'blocked_impossible_location', blockers };
  }

  // 13. Both parking (spot/lot).
  if (
    (sa === 'in_parking_spot' || sa === 'in_general_lot') &&
    (sb === 'in_parking_spot' || sb === 'in_general_lot')
  ) {
    // 16. Job-order check.
    if (context?.enforceJobOrder === true) {
      const orderBlocker = pairJobOrderBlocker(a, b);
      if (orderBlocker != null) {
        return { verdict: 'blocked_job_order', blockers: [orderBlocker] };
      }
    }
    return { verdict: 'allowed_physical_overlap', blockers: [] };
  }

  // Fallback for any state combination not explicitly handled.
  return {
    verdict: 'requires_manual_review',
    blockers: ['unknown_parallelism_policy'],
  };
}

function pairJobOrderBlocker(
  a: VehiclePhysicalRealityShape,
  b: VehiclePhysicalRealityShape,
): PhysicalRealityBlocker | null {
  const sa = getJobStage(a.job_category ?? undefined);
  const sb = getJobStage(b.job_category ?? undefined);
  // Same stage → parallel allowed by specialty-job-order doctrine.
  if (sa === sb) return null;
  // Different stages running concurrently → the LATER stage cannot
  // proceed until the earlier finishes. The validator surfaces this
  // even if the timing windows overlap — the caller must sequence.
  return 'job_order_prerequisite_not_met';
}

// =============================================================================
// Specialty-bay compatibility
// =============================================================================

export interface SpecialtyBayCompatibilityVerdict {
  ok: boolean;
  blockers: PhysicalRealityBlocker[];
}

/**
 * Validate a single ticket's specialty-bay assignment against its
 * required bay type. Pure.
 *
 *   - `required_bay_type` set + `bay_type` set + mismatch →
 *     `bay_type_mismatch`.
 *   - `required_bay_type` set + `bay_type` missing → `required_bay_unavailable`.
 *   - `view_only` ticket + `specialty_bay_id` missing →
 *     `specialty_view_not_physical_location` (defensive — a view-only
 *     entry should reference a specialty bay).
 */
export function getSpecialtyBayCompatibilityVerdict(
  ticket: VehiclePhysicalRealityShape,
): SpecialtyBayCompatibilityVerdict {
  const blockers: PhysicalRealityBlocker[] = [];

  if (ticket.required_bay_type != null) {
    if (ticket.bay_type == null) {
      pushUnique(blockers, 'required_bay_unavailable');
    } else if (ticket.bay_type !== ticket.required_bay_type) {
      pushUnique(blockers, 'bay_type_mismatch');
    }
  }

  if (ticket.view_only === true && ticket.specialty_bay_id == null) {
    pushUnique(blockers, 'specialty_view_not_physical_location');
  }

  return { ok: blockers.length === 0, blockers };
}

// =============================================================================
// Cross-ticket conflict scan
// =============================================================================

export interface VehiclePhysicalRealityConflict {
  ticketA: string;
  ticketB: string;
  vehicleId: string;
  verdict: ParallelismVerdict;
  blockers: PhysicalRealityBlocker[];
}

/**
 * Scan a set of tickets for SAME-vehicle physical-reality conflicts.
 * Pairs every ticket with every other and records conflict verdicts
 * whose `verdict !== 'allowed_physical_overlap'` AND
 * `verdict !== 'allowed_view_overlap'`.
 *
 * Pure. Does not mutate inputs.
 */
export function getVehiclePhysicalLocationConflicts(
  tickets: ReadonlyArray<VehiclePhysicalRealityShape>,
  context?: OverlapVerdictContext,
): VehiclePhysicalRealityConflict[] {
  const out: VehiclePhysicalRealityConflict[] = [];
  for (let i = 0; i < tickets.length; i++) {
    for (let j = i + 1; j < tickets.length; j++) {
      const a = tickets[i];
      const b = tickets[j];
      if (a.vehicle_id == null || b.vehicle_id == null) continue;
      if (a.vehicle_id !== b.vehicle_id) continue;
      const r = canTicketsOverlapPhysically(a, b, context);
      if (
        r.verdict === 'allowed_physical_overlap' ||
        r.verdict === 'allowed_view_overlap'
      ) {
        continue;
      }
      out.push({
        ticketA: String(a.id ?? ''),
        ticketB: String(b.id ?? ''),
        vehicleId: a.vehicle_id,
        verdict: r.verdict,
        blockers: r.blockers,
      });
    }
  }
  return out;
}

/** Boolean convenience: any conflict in the set? */
export function hasVehiclePhysicalRealityConflicts(
  tickets: ReadonlyArray<VehiclePhysicalRealityShape>,
  context?: OverlapVerdictContext,
): boolean {
  return getVehiclePhysicalLocationConflicts(tickets, context).length > 0;
}

// =============================================================================
// Top-level validators
// =============================================================================

export interface PhysicalRealityValidationResult {
  ok: boolean;
  blockers: PhysicalRealityBlocker[];
  conflicts: VehiclePhysicalRealityConflict[];
  /** Per-ticket physical-location classification. */
  perTicketStates: Array<{ ticketId: string; state: VehiclePhysicalLocationState }>;
}

/**
 * Top-level scan: classify each ticket + collect all SAME-vehicle
 * conflicts + emit blockers. Pure.
 */
export function validateVehiclePhysicalReality(
  tickets: ReadonlyArray<VehiclePhysicalRealityShape>,
  context?: OverlapVerdictContext,
): PhysicalRealityValidationResult {
  const perTicketStates: PhysicalRealityValidationResult['perTicketStates'] = [];
  for (const t of tickets) {
    perTicketStates.push({
      ticketId: String(t.id ?? ''),
      state: getVehiclePhysicalLocationState(t),
    });
  }

  const conflicts = getVehiclePhysicalLocationConflicts(tickets, context);
  const blockers: PhysicalRealityBlocker[] = [];
  for (const c of conflicts) {
    for (const b of c.blockers) pushUnique(blockers, b);
  }
  return {
    ok: conflicts.length === 0,
    blockers,
    conflicts,
    perTicketStates,
  };
}

/**
 * Top-level scan composing physical reality + (optional) job-order.
 * The job-order check fires only when the caller passes a context
 * with `enforceJobOrder: true`.
 *
 * `jobs` is informational — Phase 7.7 reads `job_category` directly
 * from each ticket; future Phase 7.8+ can extend the input shape to
 * carry richer per-ticket job lists.
 */
export function validateParallelWorkPhysicalCompatibility(
  tickets: ReadonlyArray<VehiclePhysicalRealityShape>,
  context?: OverlapVerdictContext,
): PhysicalRealityValidationResult {
  return validateVehiclePhysicalReality(tickets, context);
}

/**
 * Flat blocker list. Sister to `validateVehiclePhysicalReality.blockers`
 * for callers that prefer a single array.
 */
export function getParallelismBlockers(
  tickets: ReadonlyArray<VehiclePhysicalRealityShape>,
  context?: OverlapVerdictContext,
): PhysicalRealityBlocker[] {
  return validateVehiclePhysicalReality(tickets, context).blockers;
}

/**
 * Convenience: single pair-level conflict (or null if none). Mirrors
 * `canTicketsOverlapPhysically` for callers that want the conflict
 * shape rather than a verdict envelope.
 */
export function getVehicleBayPositionConflict(
  a: VehiclePhysicalRealityShape,
  b: VehiclePhysicalRealityShape,
  context?: OverlapVerdictContext,
): VehiclePhysicalRealityConflict | null {
  const r = canTicketsOverlapPhysically(a, b, context);
  if (
    r.verdict === 'allowed_physical_overlap' ||
    r.verdict === 'allowed_view_overlap'
  ) {
    return null;
  }
  return {
    ticketA: String(a.id ?? ''),
    ticketB: String(b.id ?? ''),
    vehicleId: String(a.vehicle_id ?? b.vehicle_id ?? ''),
    verdict: r.verdict,
    blockers: r.blockers,
  };
}

// =============================================================================
// Split-parts parallelism
// =============================================================================

/**
 * Can a set of split-sibling tickets (same `split_group_id`) run in
 * parallel without implying impossible physical reality?
 *
 * Resolution:
 *   - Filter out rollover continuations (they continue a previous
 *     part across an EOD boundary; they don't represent same-time
 *     parallel work).
 *   - Run `validateVehiclePhysicalReality` across the remaining
 *     siblings.
 *   - If any conflict surfaces → emit
 *     `split_parts_imply_impossible_parallel_location`.
 */
export function canSplitPartsRunInParallel(
  parts: ReadonlyArray<VehiclePhysicalRealityShape>,
  context?: OverlapVerdictContext,
): PhysicalRealityValidationResult {
  // Filter rollover continuations — those run in series with the
  // original part, not parallel.
  const jobLineParts = parts.filter(p => !isRolloverContinuationNotJobLineSplit(p));
  const result = validateVehiclePhysicalReality(jobLineParts, context);
  if (!result.ok) {
    const blockers = [...result.blockers];
    if (!blockers.includes('split_parts_imply_impossible_parallel_location')) {
      blockers.push('split_parts_imply_impossible_parallel_location');
    }
    return { ...result, blockers };
  }
  return result;
}

// =============================================================================
// Adoption points (documented in code; no behavior change in this slice)
// =============================================================================
//
// 1. Cascade preflight:
//    `globalCascade.ts` / `localCascade.ts` can call
//    `validateVehiclePhysicalReality(tickets)` BEFORE proposing a
//    layout change. Conflicts block the proposed layout with the
//    specific blocker emitted in the per-conflict result.
//
// 2. Smart-assign preflight:
//    `supabase/functions/smart-assign/index.ts` could refuse to
//    assign a tech to a ticket if the resulting layout would create
//    a same-vehicle conflict with an existing in-flight ticket.
//
// 3. Split creation:
//    `execute-manual-split` / `split-ticket` can run
//    `canSplitPartsRunInParallel` on the proposed child set before
//    DB writes. Rollover continuations are automatically excluded.
//
// 4. UI surfacing:
//    Birdseye / Dayboard can highlight conflict pairs returned by
//    `getVehiclePhysicalLocationConflicts` so users see "Vehicle X
//    is in Bay 3 AND Specialty Bay 7" before approving the layout.
//
// 5. Job-order enforcement:
//    When the caller wants strict job-order, pass `enforceJobOrder:
//    true` in `OverlapVerdictContext`. Same stage allows parallel;
//    different stages emit `job_order_prerequisite_not_met` so
//    callers can re-sequence.
//
// 6. Specialty bay view-entry (Phase 7.8 decision):
//    Phase 7.7 accepts `view_only: boolean` from caller context.
//    Phase 7.8 should formalize whether specialty bay entries are
//    physical or view-only by default, and surface the decision
//    via a new column / convention. The helper's `treatSpecialtyDuplicateAsView`
//    context flag provides a graceful interim path.
//
// 7. Audit:
//    Reserve `LAYOUT_BLOCKED_PHYSICAL_REALITY` /
//    `SPLIT_BLOCKED_IMPOSSIBLE_PARALLEL` in `AdminActionType` once
//    adoption begins.
